import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/ServicesScreen.scss";
import ProxyServicePanel from "../components/ProxyServicePanel";
import { getAuthHeaders, getProfile } from "../utils/auth";
import { apiFetch } from "../utils/api";

const MAIN_API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";
const EVENTS_SERVICE_URL =
  process.env.REACT_APP_EVENTS_API_URL || `${MAIN_API_BASE}/services/events`;
const UPCOMING_DAYS = 7;

const EMPTY_EVENT_FORM = {
  name: "",
  date: "",
  description: "",
  link: "",
};

const EMPTY_MEETING_FORM = {
  name: "",
  date: "",
  description: "",
  creatorId: "",
  groups: [],
};

function resolveEventsApiBase() {
  const normalized = String(EVENTS_SERVICE_URL || "http://localhost:8000").replace(
    /\/+$/,
    "",
  );
  if (normalized.endsWith("/services/events")) {
    return normalized;
  }
  return normalized.endsWith("/events") ? normalized : `${normalized}/events`;
}

const EVENTS_API_BASE = resolveEventsApiBase();

function normalizeUsername(value = "") {
  return value.toString().trim().replace(/^@/, "").toLowerCase();
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function sortByDateAsc(items) {
  return [...safeArray(items)].sort((a, b) => {
    const aTime = new Date(a?.date || 0).getTime();
    const bTime = new Date(b?.date || 0).getTime();
    return aTime - bTime;
  });
}

function sortByDateDesc(items) {
  return [...safeArray(items)].sort((a, b) => {
    const aTime = new Date(a?.date || 0).getTime();
    const bTime = new Date(b?.date || 0).getTime();
    return bTime - aTime;
  });
}

function formatDateTime(value) {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function toInputDateTimeValue(value) {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 16);
}

function getUserDisplayName(user) {
  if (!user) {
    return "";
  }
  const fullName =
    user.full_name || [user.first_name, user.last_name].filter(Boolean).join(" ");
  const username = user.username ? `@${String(user.username).replace(/^@/, "")}` : "";
  if (fullName && username) {
    return `${fullName} (${username})`;
  }
  return fullName || username || String(user.telegram_id || "");
}

async function readResponseError(response, fallback) {
  try {
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const data = await response.json();
      if (typeof data?.detail === "string" && data.detail.trim()) {
        return data.detail;
      }
      if (typeof data?.message === "string" && data.message.trim()) {
        return data.message;
      }
    } else {
      const text = await response.text();
      if (text.trim()) {
        return text.trim();
      }
    }
  } catch (error) {}
  return fallback;
}

async function requestJson(url, options = {}, fallback = "Ошибка запроса") {
  const response = await apiFetch(url, options);
  if (!response.ok) {
    throw new Error(await readResponseError(response, fallback));
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

function buildEventCreateUrl(form) {
  const params = new URLSearchParams();
  params.set("name", form.name.trim());
  params.set("date", form.date);
  if (form.description.trim()) {
    params.set("description", form.description.trim());
  }
  if (form.link.trim()) {
    params.set("link", form.link.trim());
  }
  return `${EVENTS_API_BASE}/events?${params.toString()}`;
}

function dedupeStrings(values) {
  const result = [];
  const seen = new Set();
  safeArray(values).forEach((value) => {
    const normalized = normalizeUsername(value);
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    result.push(normalized);
  });
  return result;
}

function getMeetingStatus(meeting) {
  const date = new Date(meeting?.date || 0);
  if (Number.isNaN(date.getTime())) {
    return "Неизвестно";
  }
  return date.getTime() >= Date.now() ? "Запланировано" : "Прошло";
}

export default function ServicesScreen() {
  const profile = useMemo(() => getProfile(), []);
  const authHeaders = useMemo(() => getAuthHeaders(), []);

  const [activeService, setActiveService] = useState("");
  const [activeTab, setActiveTab] = useState("events");

  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [catalogError, setCatalogError] = useState("");
  const [isCatalogLoading, setIsCatalogLoading] = useState(true);

  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [eventsError, setEventsError] = useState("");
  const [isEventsLoading, setIsEventsLoading] = useState(true);

  const [meetings, setMeetings] = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [pastMeetings, setPastMeetings] = useState([]);
  const [meetingsError, setMeetingsError] = useState("");
  const [isMeetingsLoading, setIsMeetingsLoading] = useState(true);

  const [eventForm, setEventForm] = useState(EMPTY_EVENT_FORM);
  const [meetingForm, setMeetingForm] = useState(EMPTY_MEETING_FORM);
  const [projectFilter, setProjectFilter] = useState("");

  const [eventsNotice, setEventsNotice] = useState("");
  const [eventsActionError, setEventsActionError] = useState("");
  const [meetingsNotice, setMeetingsNotice] = useState("");
  const [meetingsActionError, setMeetingsActionError] = useState("");

  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [isCreatingMeeting, setIsCreatingMeeting] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(0);
  const [deletingMeetingId, setDeletingMeetingId] = useState(0);

  const [openMeetingCards, setOpenMeetingCards] = useState({});
  const [meetingDetails, setMeetingDetails] = useState({});
  const [meetingDetailLoading, setMeetingDetailLoading] = useState({});

  const [attendanceSelections, setAttendanceSelections] = useState({});
  const [attendanceExtraPick, setAttendanceExtraPick] = useState({});
  const [attendanceSubmitting, setAttendanceSubmitting] = useState({});

  const refreshCatalog = async () => {
    setCatalogError("");
    setIsCatalogLoading(true);
    try {
      const [usersData, projectsData] = await Promise.all([
        requestJson(
          `${MAIN_API_BASE}/get_users`,
          { headers: authHeaders },
          "Не удалось загрузить пользователей",
        ),
        requestJson(
          `${MAIN_API_BASE}/projects`,
          { headers: authHeaders },
          "Не удалось загрузить проекты",
        ),
      ]);
      setUsers(safeArray(usersData));
      setProjects(safeArray(projectsData));
    } catch (error) {
      setCatalogError(error.message || "Не удалось загрузить пользователей и проекты");
    } finally {
      setIsCatalogLoading(false);
    }
  };

  const refreshEvents = async () => {
    setEventsError("");
    setIsEventsLoading(true);
    try {
      const [allEventsData, upcomingData] = await Promise.all([
        requestJson(
          `${EVENTS_API_BASE}/all_events?limit=200`,
          { headers: authHeaders },
          "Не удалось загрузить события",
        ),
        requestJson(
          `${EVENTS_API_BASE}/events/upcoming?days=${UPCOMING_DAYS}`,
          { headers: authHeaders },
          "Не удалось загрузить ближайшие события",
        ),
      ]);
      setEvents(sortByDateAsc(allEventsData));
      setUpcomingEvents(sortByDateAsc(upcomingData));
    } catch (error) {
      setEventsError(error.message || "Не удалось загрузить события");
    } finally {
      setIsEventsLoading(false);
    }
  };

  const refreshMeetings = async () => {
    setMeetingsError("");
    setIsMeetingsLoading(true);
    try {
      const [meetingsData, upcomingData, pastData] = await Promise.all([
        requestJson(
          `${EVENTS_API_BASE}/meetings?limit=200`,
          { headers: authHeaders },
          "Не удалось загрузить собрания",
        ),
        requestJson(
          `${EVENTS_API_BASE}/meetings/upcoming?days=${UPCOMING_DAYS}`,
          { headers: authHeaders },
          "Не удалось загрузить ближайшие собрания",
        ),
        requestJson(
          `${EVENTS_API_BASE}/meetings/past`,
          { headers: authHeaders },
          "Не удалось загрузить прошедшие собрания",
        ),
      ]);
      setMeetings(sortByDateAsc(meetingsData));
      setUpcomingMeetings(sortByDateAsc(upcomingData));
      setPastMeetings(sortByDateDesc(pastData));
    } catch (error) {
      setMeetingsError(error.message || "Не удалось загрузить собрания");
    } finally {
      setIsMeetingsLoading(false);
    }
  };

  const refreshAll = async () => {
    await Promise.allSettled([refreshCatalog(), refreshEvents(), refreshMeetings()]);
  };

  const openEventsService = () => {
    setActiveService("events-management");
    setActiveTab("events");
    refreshAll();
  };

  const openProxyService = () => {
    setActiveService("itc-proxy");
  };

  const closeActiveService = () => {
    setActiveService("");
  };

  const userOptions = useMemo(() => {
    return safeArray(users)
      .map((user) => {
        const username = user?.username
          ? String(user.username).replace(/^@/, "")
          : "";
        const creatorValue = String(user?.telegram_id || username || "");
        return {
          raw: user,
          username,
          normalizedUsername: normalizeUsername(username),
          creatorValue,
          telegramId: user?.telegram_id ? String(user.telegram_id) : "",
          label: getUserDisplayName(user),
        };
      })
      .filter((user) => user.creatorValue)
      .sort((a, b) => a.label.localeCompare(b.label, "ru"));
  }, [users]);

  const attendanceUserOptions = useMemo(() => {
    return userOptions.filter((user) => user.normalizedUsername);
  }, [userOptions]);

  const projectOptions = useMemo(() => {
    return safeArray(projects)
      .map((project) => ({
        id: Number(project?.id),
        title: String(project?.title || `Проект #${project?.id || "?"}`),
        status: String(project?.status || ""),
        members: safeArray(project?.members),
      }))
      .filter((project) => Number.isFinite(project.id))
      .sort((a, b) => a.title.localeCompare(b.title, "ru"));
  }, [projects]);

  const projectsById = useMemo(() => {
    const map = {};
    projectOptions.forEach((project) => {
      map[project.id] = project;
    });
    return map;
  }, [projectOptions]);

  const currentUserOption = useMemo(() => {
    const profileTelegramId = profile?.telegram_id ? String(profile.telegram_id) : "";
    const profileUsername = profile?.username ? normalizeUsername(profile.username) : "";

    return (
      userOptions.find(
        (user) =>
          (profileTelegramId && user.telegramId === profileTelegramId) ||
          (profileUsername && user.normalizedUsername === profileUsername),
      ) || null
    );
  }, [profile, userOptions]);

  useEffect(() => {
    if (!currentUserOption?.creatorValue) {
      return;
    }
    setMeetingForm((prev) => {
      if (prev.creatorId) {
        return prev;
      }
      return { ...prev, creatorId: currentUserOption.creatorValue };
    });
  }, [currentUserOption]);

  const filteredProjectOptions = useMemo(() => {
    const term = projectFilter.trim().toLowerCase();
    if (!term) {
      return projectOptions;
    }
    return projectOptions.filter((project) => {
      return (
        project.title.toLowerCase().includes(term) ||
        String(project.id).includes(term)
      );
    });
  }, [projectFilter, projectOptions]);

  const eventIdsUpcoming = useMemo(() => {
    return new Set(safeArray(upcomingEvents).map((event) => Number(event?.id)));
  }, [upcomingEvents]);

  const meetingIdsUpcoming = useMemo(() => {
    return new Set(safeArray(upcomingMeetings).map((meeting) => Number(meeting?.id)));
  }, [upcomingMeetings]);

  const meetingIdsPast = useMemo(() => {
    return new Set(safeArray(pastMeetings).map((meeting) => Number(meeting?.id)));
  }, [pastMeetings]);

  const handleEventFieldChange = (field, value) => {
    setEventForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleMeetingFieldChange = (field, value) => {
    setMeetingForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleMeetingGroup = (projectId) => {
    setMeetingForm((prev) => {
      const exists = prev.groups.includes(projectId);
      return {
        ...prev,
        groups: exists
          ? prev.groups.filter((id) => id !== projectId)
          : [...prev.groups, projectId],
      };
    });
  };

  const selectAllFilteredGroups = () => {
    const ids = filteredProjectOptions.map((project) => project.id);
    setMeetingForm((prev) => ({
      ...prev,
      groups: Array.from(new Set([...prev.groups, ...ids])),
    }));
  };

  const clearAllGroups = () => {
    setMeetingForm((prev) => ({ ...prev, groups: [] }));
  };

  const handleCreateEvent = async (event) => {
    event.preventDefault();
    setEventsActionError("");
    setEventsNotice("");

    if (!eventForm.name.trim()) {
      setEventsActionError("Укажите название события");
      return;
    }
    if (!eventForm.date) {
      setEventsActionError("Укажите дату и время события");
      return;
    }

    setIsCreatingEvent(true);
    try {
      await requestJson(
        buildEventCreateUrl(eventForm),
        {
          method: "POST",
          headers: authHeaders,
        },
        "Не удалось создать событие",
      );
      setEventForm(EMPTY_EVENT_FORM);
      setEventsNotice("Событие создано");
      await refreshEvents();
    } catch (error) {
      setEventsActionError(error.message || "Не удалось создать событие");
    } finally {
      setIsCreatingEvent(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm(`Удалить событие #${eventId}?`)) {
      return;
    }
    setEventsActionError("");
    setEventsNotice("");
    setDeletingEventId(eventId);
    try {
      await requestJson(
        `${EVENTS_API_BASE}/events/${eventId}`,
        {
          method: "DELETE",
          headers: authHeaders,
        },
        "Не удалось удалить событие",
      );
      setEventsNotice(`Событие #${eventId} удалено`);
      setEvents((prev) => prev.filter((item) => Number(item?.id) !== Number(eventId)));
      setUpcomingEvents((prev) =>
        prev.filter((item) => Number(item?.id) !== Number(eventId)),
      );
    } catch (error) {
      setEventsActionError(error.message || "Не удалось удалить событие");
    } finally {
      setDeletingEventId(0);
    }
  };

  const handleCreateMeeting = async (event) => {
    event.preventDefault();
    setMeetingsActionError("");
    setMeetingsNotice("");

    if (!meetingForm.name.trim()) {
      setMeetingsActionError("Укажите название собрания");
      return;
    }
    if (!meetingForm.date) {
      setMeetingsActionError("Укажите дату и время собрания");
      return;
    }
    if (!meetingForm.creatorId.trim()) {
      setMeetingsActionError("Выберите создателя собрания");
      return;
    }

    setIsCreatingMeeting(true);
    try {
      await requestJson(
        `${EVENTS_API_BASE}/meetings`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify({
            name: meetingForm.name.trim(),
            date: meetingForm.date,
            description: meetingForm.description.trim() || null,
            creator_id: meetingForm.creatorId.trim(),
            groups: meetingForm.groups,
          }),
        },
        "Не удалось создать собрание",
      );
      setMeetingForm((prev) => ({
        ...EMPTY_MEETING_FORM,
        creatorId: prev.creatorId || currentUserOption?.creatorValue || "",
      }));
      setProjectFilter("");
      setMeetingsNotice("Собрание создано");
      await refreshMeetings();
    } catch (error) {
      setMeetingsActionError(error.message || "Не удалось создать собрание");
    } finally {
      setIsCreatingMeeting(false);
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm(`Удалить собрание #${meetingId}?`)) {
      return;
    }
    setMeetingsActionError("");
    setMeetingsNotice("");
    setDeletingMeetingId(meetingId);
    try {
      await requestJson(
        `${EVENTS_API_BASE}/meetings/${meetingId}`,
        {
          method: "DELETE",
          headers: authHeaders,
        },
        "Не удалось удалить собрание",
      );
      setMeetingsNotice(`Собрание #${meetingId} удалено`);
      setMeetings((prev) => prev.filter((item) => Number(item?.id) !== Number(meetingId)));
      setUpcomingMeetings((prev) =>
        prev.filter((item) => Number(item?.id) !== Number(meetingId)),
      );
      setPastMeetings((prev) => prev.filter((item) => Number(item?.id) !== Number(meetingId)));
      setMeetingDetails((prev) => {
        const next = { ...prev };
        delete next[meetingId];
        return next;
      });
    } catch (error) {
      setMeetingsActionError(error.message || "Не удалось удалить собрание");
    } finally {
      setDeletingMeetingId(0);
    }
  };

  const loadMeetingDetail = async (meetingId) => {
    setMeetingDetailLoading((prev) => ({ ...prev, [meetingId]: true }));
    setMeetingsActionError("");
    try {
      const detail = await requestJson(
        `${EVENTS_API_BASE}/meetings/${meetingId}`,
        { headers: authHeaders },
        `Не удалось загрузить детали собрания #${meetingId}`,
      );
      setMeetingDetails((prev) => ({ ...prev, [meetingId]: detail }));
    } catch (error) {
      setMeetingsActionError(error.message || `Не удалось загрузить детали собрания #${meetingId}`);
    } finally {
      setMeetingDetailLoading((prev) => ({ ...prev, [meetingId]: false }));
    }
  };

  const toggleMeetingCard = (meetingId) => {
    setOpenMeetingCards((prev) => {
      const nextOpen = !prev[meetingId];
      return { ...prev, [meetingId]: nextOpen };
    });
    if (!meetingDetails[meetingId] && !meetingDetailLoading[meetingId]) {
      loadMeetingDetail(meetingId);
    }
  };

  const getMeetingCandidateUsers = (meeting) => {
    const usernames = new Set();
    const fallbackUsers = [];
    safeArray(meeting?.groups).forEach((groupId) => {
      const project = projectsById[Number(groupId)];
      if (!project) {
        return;
      }
      safeArray(project.members).forEach((member) => {
        const normalized = normalizeUsername(member?.username || "");
        if (!normalized || usernames.has(normalized)) {
          return;
        }
        usernames.add(normalized);
        fallbackUsers.push({
          normalizedUsername: normalized,
          username: String(member?.username || normalized),
          label:
            member?.full_name ||
            String(member?.username || normalized).replace(/^@/, "") ||
            normalized,
        });
      });
    });

    const combined = fallbackUsers.map((member) => {
      const matched = attendanceUserOptions.find(
        (option) => option.normalizedUsername === member.normalizedUsername,
      );
      return matched
        ? {
            normalizedUsername: matched.normalizedUsername,
            username: matched.username,
            label: matched.label,
          }
        : member;
    });

    return combined.sort((a, b) => a.label.localeCompare(b.label, "ru"));
  };

  const toggleAttendanceSelection = (meetingId, username) => {
    const normalized = normalizeUsername(username);
    if (!normalized) {
      return;
    }
    setAttendanceSelections((prev) => {
      const current = safeArray(prev[meetingId]);
      const exists = current.includes(normalized);
      return {
        ...prev,
        [meetingId]: exists
          ? current.filter((item) => item !== normalized)
          : [...current, normalized],
      };
    });
  };

  const addAttendanceExtraPick = (meetingId) => {
    const picked = attendanceExtraPick[meetingId];
    const normalized = normalizeUsername(picked || "");
    if (!normalized) {
      return;
    }
    setAttendanceSelections((prev) => ({
      ...prev,
      [meetingId]: dedupeStrings([...(prev[meetingId] || []), normalized]),
    }));
    setAttendanceExtraPick((prev) => ({ ...prev, [meetingId]: "" }));
  };

  const markAttendanceForMeeting = async (meeting) => {
    const meetingId = Number(meeting?.id);
    const selected = dedupeStrings(attendanceSelections[meetingId] || []);
    if (!selected.length) {
      setMeetingsActionError("Выберите хотя бы одного пользователя для отметки присутствия");
      return;
    }

    const alreadyPresent = new Set(
      safeArray(meeting?.attendance).map((item) => normalizeUsername(item)),
    );
    const toSubmit = selected.filter((username) => !alreadyPresent.has(username));

    if (!toSubmit.length) {
      setMeetingsActionError("Все выбранные пользователи уже отмечены на этом собрании");
      return;
    }

    setMeetingsActionError("");
    setMeetingsNotice("");
    setAttendanceSubmitting((prev) => ({ ...prev, [meetingId]: true }));

    try {
      for (const username of toSubmit) {
        const url = `${EVENTS_API_BASE}/meetings/${meetingId}/attendance?username=${encodeURIComponent(
          username,
        )}`;
        await requestJson(
          url,
          {
            method: "POST",
            headers: authHeaders,
          },
          `Не удалось отметить посещаемость для @${username}`,
        );
      }

      const mergedAttendance = dedupeStrings([
        ...safeArray(meeting?.attendance),
        ...toSubmit,
      ]);
      setMeetings((prev) =>
        prev.map((item) =>
          Number(item?.id) === meetingId ? { ...item, attendance: mergedAttendance } : item,
        ),
      );
      setUpcomingMeetings((prev) =>
        prev.map((item) =>
          Number(item?.id) === meetingId ? { ...item, attendance: mergedAttendance } : item,
        ),
      );
      setPastMeetings((prev) =>
        prev.map((item) =>
          Number(item?.id) === meetingId ? { ...item, attendance: mergedAttendance } : item,
        ),
      );
      setMeetingDetails((prev) => {
        if (!prev[meetingId]) {
          return prev;
        }
        return {
          ...prev,
          [meetingId]: {
            ...prev[meetingId],
            attendance: mergedAttendance,
          },
        };
      });
      setMeetingsNotice(
        `Отмечено присутствие: ${toSubmit.map((name) => `@${name}`).join(", ")}`,
      );
      setAttendanceSelections((prev) => ({ ...prev, [meetingId]: [] }));
      await loadMeetingDetail(meetingId);
    } catch (error) {
      setMeetingsActionError(
        error.message || "Не удалось отметить присутствие участников",
      );
    } finally {
      setAttendanceSubmitting((prev) => ({ ...prev, [meetingId]: false }));
    }
  };

  const renderFeedback = (type, message) => {
    if (!message) {
      return null;
    }
    return <p className={`services-feedback services-feedback--${type}`}>{message}</p>;
  };

  const renderEventCard = (item, index) => {
    const isUpcoming = eventIdsUpcoming.has(Number(item?.id));
    return (
      <article className="service-card" key={`event-${item?.id || index}`}>
        <div className="service-card__content">
          <div className="service-card__head">
            <div>
              <p className="service-card__eyebrow">Событие #{item?.id ?? "—"}</p>
              <h3 className="service-card__title">{item?.name || "Без названия"}</h3>
            </div>
            <div className="service-card__chips">
              <span className={`service-chip${isUpcoming ? " is-accent" : ""}`}>
                {isUpcoming ? `Ближайшее (${UPCOMING_DAYS} дн.)` : "Общее"}
              </span>
              <span className="service-chip">{formatDateTime(item?.date)}</span>
            </div>
          </div>
          {item?.description ? (
            <p className="service-card__description">{item.description}</p>
          ) : (
            <p className="service-card__description is-muted">Описание не указано</p>
          )}
          <div className="service-card__meta-row">
            {item?.link ? (
              <a
                className="service-card__link"
                href={item.link}
                target="_blank"
                rel="noreferrer"
              >
                Открыть ссылку
              </a>
            ) : (
              <span className="service-card__meta-placeholder">Без ссылки</span>
            )}
          </div>
        </div>
        <div className="service-card__actions">
          <button
            type="button"
            className="service-btn service-btn--danger"
            disabled={deletingEventId === Number(item?.id)}
            onClick={() => handleDeleteEvent(Number(item?.id))}
          >
            {deletingEventId === Number(item?.id) ? "Удаление..." : "Удалить"}
          </button>
        </div>
      </article>
    );
  };

  const renderMeetingCard = (meeting, index) => {
    const meetingId = Number(meeting?.id);
    const detail = meetingDetails[meetingId];
    const isOpen = Boolean(openMeetingCards[meetingId]);
    const isLoadingDetail = Boolean(meetingDetailLoading[meetingId]);
    const attendanceList = safeArray(detail?.attendance || meeting?.attendance);
    const candidateUsers = getMeetingCandidateUsers(meeting);
    const selectedAttendance = dedupeStrings(attendanceSelections[meetingId] || []);
    const isUpcoming = meetingIdsUpcoming.has(meetingId);
    const isPast = meetingIdsPast.has(meetingId);
    const creatorLabel =
      userOptions.find((option) => option.creatorValue === String(meeting?.creator_id || ""))
        ?.label || String(meeting?.creator_id || "—");

    return (
      <article className="service-card service-card--meeting" key={`meeting-${meetingId || index}`}>
        <div className="service-card__content">
          <div className="service-card__head">
            <div>
              <p className="service-card__eyebrow">Собрание #{meeting?.id ?? "—"}</p>
              <h3 className="service-card__title">{meeting?.name || "Без названия"}</h3>
            </div>
            <div className="service-card__chips">
              <span className={`service-chip${isUpcoming ? " is-accent" : ""}`}>
                {isUpcoming ? "Ближайшее" : isPast ? "Прошедшее" : getMeetingStatus(meeting)}
              </span>
              <span className="service-chip">{formatDateTime(meeting?.date)}</span>
            </div>
          </div>

          <p className="service-card__description">
            {meeting?.description || "Описание не указано"}
          </p>

          <div className="service-card__meta-grid">
            <div>
              <span className="service-card__meta-label">Создатель</span>
              <p className="service-card__meta-value">{creatorLabel}</p>
            </div>
            <div>
              <span className="service-card__meta-label">Группы / проекты</span>
              <div className="service-card__tags">
                {safeArray(meeting?.groups).length ? (
                  safeArray(meeting.groups).map((groupId) => {
                    const project = projectsById[Number(groupId)];
                    return project ? (
                      <Link
                        key={`${meetingId}-group-${groupId}`}
                        className="service-tag service-tag--link"
                        to={`/projects/${project.id}`}
                      >
                        {project.title}
                      </Link>
                    ) : (
                      <span key={`${meetingId}-group-${groupId}`} className="service-tag">
                        Проект #{groupId}
                      </span>
                    );
                  })
                ) : (
                  <span className="service-tag is-muted">Без привязки к проектам</span>
                )}
              </div>
            </div>
            <div>
              <span className="service-card__meta-label">Посещаемость</span>
              <p className="service-card__meta-value">
                {attendanceList.length} {attendanceList.length === 1 ? "участник" : "участников"}
              </p>
            </div>
            <div>
              <span className="service-card__meta-label">Отчет</span>
              <p className="service-card__meta-value">
                {meeting?.report_sent ? "Отправлен" : "Не отправлен"}
              </p>
            </div>
          </div>

          <div className="service-card__toolbar">
            <button
              type="button"
              className="service-btn"
              onClick={() => toggleMeetingCard(meetingId)}
            >
              {isOpen ? "Скрыть детали" : "Управление посещаемостью"}
            </button>
            <button
              type="button"
              className="service-btn service-btn--ghost"
              onClick={() => loadMeetingDetail(meetingId)}
              disabled={isLoadingDetail}
            >
              {isLoadingDetail ? "Обновление..." : "Обновить детали"}
            </button>
          </div>

          {isOpen ? (
            <div className="meeting-attendance-panel">
              <div className="meeting-attendance-panel__section">
                <div className="meeting-attendance-panel__head">
                  <h4>Текущие отметки</h4>
                  <span>{attendanceList.length} шт.</span>
                </div>
                <div className="meeting-attendance-panel__chips">
                  {attendanceList.length ? (
                    attendanceList.map((username) => (
                      <span key={`${meetingId}-att-${username}`} className="service-tag">
                        @{normalizeUsername(username) || username}
                      </span>
                    ))
                  ) : (
                    <span className="service-tag is-muted">Пока никто не отмечен</span>
                  )}
                </div>
              </div>

              <div className="meeting-attendance-panel__section">
                <div className="meeting-attendance-panel__head">
                  <h4>Выбор участников для отметки</h4>
                  <span>
                    {selectedAttendance.length
                      ? `Выбрано: ${selectedAttendance.length}`
                      : "Ничего не выбрано"}
                  </span>
                </div>

                {candidateUsers.length ? (
                  <div className="meeting-attendance-panel__checklist">
                    {candidateUsers.map((user) => {
                      const checked = selectedAttendance.includes(user.normalizedUsername);
                      return (
                        <label
                          className={`service-check${checked ? " is-checked" : ""}`}
                          key={`${meetingId}-candidate-${user.normalizedUsername}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() =>
                              toggleAttendanceSelection(meetingId, user.normalizedUsername)
                            }
                          />
                          <span>{user.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <p className="meeting-attendance-panel__hint">
                    Нет кандидатов из связанных проектов. Можно добавить пользователя вручную ниже.
                  </p>
                )}

                <div className="meeting-attendance-panel__picker-row">
                  <select
                    value={attendanceExtraPick[meetingId] || ""}
                    onChange={(event) =>
                      setAttendanceExtraPick((prev) => ({
                        ...prev,
                        [meetingId]: event.target.value,
                      }))
                    }
                  >
                    <option value="">Добавить пользователя вручную...</option>
                    {attendanceUserOptions.map((user) => (
                      <option key={`${meetingId}-pick-${user.normalizedUsername}`} value={user.normalizedUsername}>
                        {user.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="service-btn service-btn--ghost"
                    onClick={() => addAttendanceExtraPick(meetingId)}
                  >
                    Добавить
                  </button>
                  <button
                    type="button"
                    className="service-btn"
                    onClick={() => markAttendanceForMeeting(meeting)}
                    disabled={attendanceSubmitting[meetingId]}
                  >
                    {attendanceSubmitting[meetingId]
                      ? "Отмечаем..."
                      : `Отметить выбранных (${selectedAttendance.length})`}
                  </button>
                </div>
              </div>

              {detail?.attendance_summary ? (
                <div className="meeting-attendance-panel__section">
                  <div className="meeting-attendance-panel__head">
                    <h4>Сводка посещаемости</h4>
                  </div>
                  <div className="meeting-attendance-panel__summary-grid">
                    {Object.entries(detail.attendance_summary).map(([key, value]) => (
                      <div className="service-kv" key={`${meetingId}-summary-${key}`}>
                        <span>{key}</span>
                        <strong>{String(value)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="service-card__actions">
          <button
            type="button"
            className="service-btn service-btn--danger"
            disabled={deletingMeetingId === meetingId}
            onClick={() => handleDeleteMeeting(meetingId)}
          >
            {deletingMeetingId === meetingId ? "Удаление..." : "Удалить"}
          </button>
        </div>
      </article>
    );
  };

  const eventsSection = (
    <div className="services-pane">
      <section className="services-block services-block--form">
        <div className="services-block__head">
          <div>
            <p className="services-block__eyebrow">События</p>
            <h3 className="services-block__title">Создать событие</h3>
          </div>
          <button
            type="button"
            className="service-btn service-btn--ghost"
            onClick={refreshEvents}
            disabled={isEventsLoading}
          >
            {isEventsLoading ? "Обновление..." : "Обновить события"}
          </button>
        </div>

        <form className="services-form" onSubmit={handleCreateEvent}>
          <label className="services-field">
            <span>Название</span>
            <input
              type="text"
              value={eventForm.name}
              onChange={(event) => handleEventFieldChange("name", event.target.value)}
              placeholder="Например, Demo Day"
              required
            />
          </label>

          <label className="services-field">
            <span>Дата и время</span>
            <input
              type="datetime-local"
              value={eventForm.date}
              onChange={(event) => handleEventFieldChange("date", event.target.value)}
              required
            />
          </label>

          <label className="services-field services-field--wide">
            <span>Описание</span>
            <textarea
              value={eventForm.description}
              onChange={(event) => handleEventFieldChange("description", event.target.value)}
              rows={3}
              placeholder="Короткое описание события"
            />
          </label>

          <label className="services-field services-field--wide">
            <span>Ссылка</span>
            <input
              type="url"
              value={eventForm.link}
              onChange={(event) => handleEventFieldChange("link", event.target.value)}
              placeholder="https://..."
            />
          </label>

          <div className="services-form__actions">
            <button type="submit" className="service-btn" disabled={isCreatingEvent}>
              {isCreatingEvent ? "Создание..." : "Создать событие"}
            </button>
            <button
              type="button"
              className="service-btn service-btn--ghost"
              onClick={() => {
                setEventForm(EMPTY_EVENT_FORM);
                setEventsActionError("");
                setEventsNotice("");
              }}
            >
              Очистить
            </button>
          </div>
        </form>

        {renderFeedback("error", eventsError || eventsActionError)}
        {renderFeedback("success", eventsNotice)}
      </section>

      <section className="services-block">
        <div className="services-block__head">
          <div>
            <p className="services-block__eyebrow">Список</p>
            <h3 className="services-block__title">Все события</h3>
          </div>
          <div className="services-block__stat-row">
            <span className="service-chip">Всего: {events.length}</span>
            <span className="service-chip is-accent">Ближайшие: {upcomingEvents.length}</span>
          </div>
        </div>

        {isEventsLoading ? (
          <p className="services-placeholder">Загрузка событий...</p>
        ) : events.length ? (
          <div className="services-list">{events.map(renderEventCard)}</div>
        ) : (
          <p className="services-placeholder">Событий пока нет</p>
        )}
      </section>
    </div>
  );

  const meetingsSorted = useMemo(() => sortByDateAsc(meetings), [meetings]);

  const meetingsSection = (
    <div className="services-pane">
      <section className="services-block services-block--form">
        <div className="services-block__head">
          <div>
            <p className="services-block__eyebrow">Собрания</p>
            <h3 className="services-block__title">Создать собрание</h3>
          </div>
          <button
            type="button"
            className="service-btn service-btn--ghost"
            onClick={refreshMeetings}
            disabled={isMeetingsLoading}
          >
            {isMeetingsLoading ? "Обновление..." : "Обновить собрания"}
          </button>
        </div>

        <form className="services-form" onSubmit={handleCreateMeeting}>
          <label className="services-field">
            <span>Название</span>
            <input
              type="text"
              value={meetingForm.name}
              onChange={(event) => handleMeetingFieldChange("name", event.target.value)}
              placeholder="Еженедельный созвон"
              required
            />
          </label>

          <label className="services-field">
            <span>Дата и время</span>
            <input
              type="datetime-local"
              value={meetingForm.date}
              onChange={(event) => handleMeetingFieldChange("date", event.target.value)}
              min={toInputDateTimeValue(new Date())}
              required
            />
          </label>

          <label className="services-field services-field--wide">
            <span>Организатор</span>
            <select
              value={meetingForm.creatorId}
              onChange={(event) => handleMeetingFieldChange("creatorId", event.target.value)}
              required
            >
              <option value="">Выберите пользователя</option>
              {userOptions.map((user) => (
                <option key={`creator-${user.creatorValue}`} value={user.creatorValue}>
                  {user.label}
                </option>
              ))}
            </select>
          </label>

          <label className="services-field services-field--wide">
            <span>Описание</span>
            <textarea
              value={meetingForm.description}
              onChange={(event) =>
                handleMeetingFieldChange("description", event.target.value)
              }
              rows={3}
              placeholder="Что обсуждаем, какие вопросы готовим"
            />
          </label>

          <div className="services-field services-field--wide">
            <div className="services-field__row-head">
              <span>Группы / проекты ({meetingForm.groups.length})</span>
              <div className="services-inline-actions">
                <button type="button" className="service-btn service-btn--tiny" onClick={selectAllFilteredGroups}>
                  Выбрать видимые
                </button>
                <button type="button" className="service-btn service-btn--tiny service-btn--ghost" onClick={clearAllGroups}>
                  Очистить
                </button>
              </div>
            </div>
            <input
              type="search"
              value={projectFilter}
              onChange={(event) => setProjectFilter(event.target.value)}
              placeholder="Поиск проекта по названию или ID"
              className="services-search"
            />
            <div className="services-check-grid">
              {filteredProjectOptions.length ? (
                filteredProjectOptions.map((project) => {
                  const checked = meetingForm.groups.includes(project.id);
                  return (
                    <label
                      key={`group-${project.id}`}
                      className={`service-check${checked ? " is-checked" : ""}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMeetingGroup(project.id)}
                      />
                      <span>{project.title}</span>
                      <small>#{project.id}{project.status ? ` · ${project.status}` : ""}</small>
                    </label>
                  );
                })
              ) : (
                <p className="services-placeholder">Проекты не найдены</p>
              )}
            </div>
          </div>

          <div className="services-form__actions">
            <button type="submit" className="service-btn" disabled={isCreatingMeeting}>
              {isCreatingMeeting ? "Создание..." : "Создать собрание"}
            </button>
            <button
              type="button"
              className="service-btn service-btn--ghost"
              onClick={() => {
                setMeetingForm((prev) => ({
                  ...EMPTY_MEETING_FORM,
                  creatorId: prev.creatorId || currentUserOption?.creatorValue || "",
                }));
                setProjectFilter("");
                setMeetingsActionError("");
                setMeetingsNotice("");
              }}
            >
              Очистить
            </button>
          </div>
        </form>

        {renderFeedback("error", catalogError || meetingsError || meetingsActionError)}
        {renderFeedback("success", meetingsNotice)}
      </section>

      <section className="services-block">
        <div className="services-block__head">
          <div>
            <p className="services-block__eyebrow">Управление</p>
            <h3 className="services-block__title">Собрания</h3>
          </div>
          <div className="services-block__stat-row">
            <span className="service-chip">Все: {meetingsSorted.length}</span>
            <span className="service-chip is-accent">Ближайшие: {upcomingMeetings.length}</span>
            <span className="service-chip">Прошедшие: {pastMeetings.length}</span>
          </div>
        </div>

        {isMeetingsLoading ? (
          <p className="services-placeholder">Загрузка собраний...</p>
        ) : meetingsSorted.length ? (
          <div className="services-list">{meetingsSorted.map(renderMeetingCard)}</div>
        ) : (
          <p className="services-placeholder">Собраний пока нет</p>
        )}
      </section>
    </div>
  );

  if (!activeService) {
    return (
      <section className="services-screen">
        <header className="services-screen__header">
          <div>
            <p className="services-screen__eyebrow">Сервисы</p>
            <h2 className="services-screen__title">Доступные сервисы</h2>
            <p className="services-screen__subtitle">
              Выберите нужный инструмент. Со временем здесь появятся другие сервисы.
            </p>
          </div>
        </header>

        <div className="services-catalog">
          <button
            type="button"
            className="services-launcher-card services-launcher-card--proxy"
            onClick={openProxyService}
          >
            <span className="services-launcher-card__eyebrow">Инструмент</span>
            <h3 className="services-launcher-card__title">ITC VPN — VPN для участников</h3>
            <p className="services-launcher-card__description">
              Выдача подписки, статус доступа, лимит устройств и список активных IP.
            </p>
            <span className="services-launcher-card__action">Открыть</span>
          </button>
          <button
            type="button"
            className="services-launcher-card"
            onClick={openEventsService}
          >
            <span className="services-launcher-card__eyebrow">Инструмент</span>
            <h3 className="services-launcher-card__title">
              Создание событий / мероприятий
            </h3>
            <p className="services-launcher-card__description">
              Создание событий, планирование собраний и отметка посещаемости участников.
            </p>
            <span className="services-launcher-card__action">Открыть</span>
          </button>
        </div>
      </section>
    );
  }

  if (activeService === "itc-proxy") {
    return (
      <section className="services-screen">
        <header className="services-screen__header">
          <div>
            <div className="services-screen__backline">
              <button
                type="button"
                className="service-btn service-btn--ghost service-btn--tiny"
                onClick={closeActiveService}
              >
                Назад к сервисам
              </button>
            </div>
            <h2 className="services-screen__title">ITC VPN</h2>
            <p className="services-screen__subtitle">
              Персональный VPN-доступ для участников сообщества: подписка, статус и активные устройства. Поддержка: @FinItcSupportBot.
            </p>
          </div>
        </header>
        <ProxyServicePanel apiBase={MAIN_API_BASE} authHeaders={authHeaders} profile={profile} />
      </section>
    );
  }

  return (
    <section className="services-screen">
      <header className="services-screen__header">
        <div>
          <div className="services-screen__backline">
            <button
              type="button"
              className="service-btn service-btn--ghost service-btn--tiny"
              onClick={closeActiveService}
            >
              Назад к сервисам
            </button>
          </div>
          <h2 className="services-screen__title">Создание событий / мероприятий</h2>
        </div>
        <div className="services-screen__toolbar">
          <div className="services-tabs" role="tablist" aria-label="Разделы сервиса">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "events"}
              className={`services-tab${activeTab === "events" ? " is-active" : ""}`}
              onClick={() => setActiveTab("events")}
            >
              События
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "meetings"}
              className={`services-tab${activeTab === "meetings" ? " is-active" : ""}`}
              onClick={() => setActiveTab("meetings")}
            >
              Собрания
            </button>
          </div>
        </div>
      </header>

      {(isCatalogLoading && activeTab === "meetings") ||
      (isCatalogLoading && !projects.length && !users.length) ? (
        <p className="services-placeholder services-placeholder--page">
          Загрузка данных...
        </p>
      ) : activeTab === "events" ? (
        eventsSection
      ) : (
        meetingsSection
      )}
    </section>
  );
}
